import {
  CODE_LANGUAGE_DATA,
  PROGRAMMING_LANGUAGE_TYPE
} from "@/features/automationAPI/constants";
import { IApiKey } from "@/features/automationAPI/type";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

const sourceLanguage = [
  { locale: "en_US", language: "English - US" },
  { locale: "en_UK", language: "English - UK" },
  { locale: "en_IN", language: "English - India" },
  { locale: "en_SCOTT", language: "English - Scottish" },
  { locale: "en_AU", language: "English - Australia" },
  { locale: "fr_FR", language: "French - France" },
  { locale: "de_DE", language: "German" },
  { locale: "es_ES", language: "Spanish - Spain" },
  { locale: "es_MX", language: "Spanish - Mexico" },
  { locale: "it_IT", language: "Italian" },
  { locale: "pt_BR", language: "Portuguese - Brazil" },
  { locale: "pl_PL", language: "Polish" },
  { locale: "hi_IN", language: "Hindi" },
  { locale: "ko_KR", language: "Korean" },
  { locale: "ja_JP", language: "Japanese" },
  { locale: "zh_CN", language: "Chinese - Mandarin" },
  { locale: "nl_NL", language: "Dutch" },
  { locale: "fi_FI", language: "Finnish" },
  { locale: "ru_RU", language: "Russian" },
  { locale: "tr_TR", language: "Turkish" },
  { locale: "uk_UA", language: "Ukrainian" }
];

const targetLanguage = [
  { locale: "en_US", language: "English - US", supports: ["AUTOMATED", "QA"] },
  { locale: "en_UK", language: "English - UK", supports: ["AUTOMATED", "QA"] },
  {
    locale: "en_IN",
    language: "English - India",
    supports: ["AUTOMATED", "QA"]
  },
  {
    locale: "en_SCOTT",
    language: "English - Scottish",
    supports: ["AUTOMATED", "QA"]
  },
  {
    locale: "en_AU",
    language: "English - Australia",
    supports: ["AUTOMATED", "QA"]
  },
  {
    locale: "fr_FR",
    language: "French - France",
    supports: ["AUTOMATED", "QA"]
  },
  { locale: "de_DE", language: "German", supports: ["AUTOMATED", "QA"] },
  {
    locale: "es_ES",
    language: "Spanish - Spain",
    supports: ["AUTOMATED", "QA"]
  },
  {
    locale: "es_MX",
    language: "Spanish - Mexico",
    supports: ["AUTOMATED", "QA"]
  },
  { locale: "it_IT", language: "Italian", supports: ["AUTOMATED", "QA"] },
  {
    locale: "pt_BR",
    language: "Portuguese - Brazil",
    supports: ["AUTOMATED", "QA"]
  },
  { locale: "pl_PL", language: "Polish", supports: ["AUTOMATED", "QA"] },
  { locale: "hi_IN", language: "Hindi", supports: ["AUTOMATED", "QA"] },
  { locale: "ko_KR", language: "Korean", supports: ["AUTOMATED", "QA"] },
  { locale: "ta_IN", language: "Tamil", supports: ["AUTOMATED", "QA"] },
  { locale: "bn_IN", language: "Bengali", supports: ["AUTOMATED", "QA"] },
  { locale: "ja_JP", language: "Japanese", supports: ["AUTOMATED", "QA"] },
  {
    locale: "zh_CN",
    language: "Chinese - Mandarin",
    supports: ["AUTOMATED", "QA"]
  },
  { locale: "nl_NL", language: "Dutch", supports: ["AUTOMATED", "QA"] },
  { locale: "fi_FI", language: "Finnish", supports: ["AUTOMATED"] },
  { locale: "ru_RU", language: "Russian", supports: ["AUTOMATED"] },
  { locale: "tr_TR", language: "Turkish", supports: ["AUTOMATED"] },
  { locale: "da_DK", language: "Danish", supports: ["AUTOMATED"] },
  { locale: "id_ID", language: "Indonesian", supports: ["AUTOMATED"] },
  { locale: "ro_RO", language: "Romanian", supports: ["AUTOMATED"] },
  { locale: "nb_NO", language: "Norwegian", supports: ["AUTOMATED"] }
];

export interface AutomationApiSliceState {
  allApiKeys: IApiKey[];
  supportedSourceLanguage?: { locale: string; language: string }[];
  supportedTargetLanguage?: {
    locale: string;
    language: string;
    supports: string[];
  }[];
  docsState: {
    selectedProjectId?: string;
    selectedSourceLocale?: string | null;
    selectedTargetLocales?: string[];
    preferredCodeLanguage: PROGRAMMING_LANGUAGE_TYPE;
  };
}

const initialState = {
  allApiKeys: [],
  supportedSourceLanguage: sourceLanguage,
  supportedTargetLanguage: targetLanguage,
  docsState: {
    preferredCodeLanguage: CODE_LANGUAGE_DATA[0]
  }
};

export const automationApiSlice = createSlice({
  name: "automationApi",
  initialState: initialState as AutomationApiSliceState,
  reducers: {
    setAllApiKeys: (
      state,
      action: PayloadAction<AutomationApiSliceState["allApiKeys"]>
    ) => {
      state.allApiKeys = action.payload;
    },
    addApiKey: (state, action: PayloadAction<IApiKey>) => {
      console.log(action.payload);
      state.allApiKeys = [action.payload, ...state.allApiKeys];
    },
    updateApiKey: (
      state,
      action: PayloadAction<{ id: string; newName: string }>
    ) => {
      state.allApiKeys = [...state.allApiKeys].map((apiKey) => {
        if (apiKey.id === action.payload.id) {
          return {
            ...apiKey,
            name: action.payload.newName
          };
        }
        return apiKey;
      });
    },
    deleteApiKey: (state, action: PayloadAction<string>) => {
      state.allApiKeys = [...state.allApiKeys].filter(
        (apiKey) => apiKey.id !== action.payload
      );
    },
    setSupportedSourceLanguage: (
      state,
      action: PayloadAction<AutomationApiSliceState["supportedSourceLanguage"]>
    ) => {
      state.supportedSourceLanguage = action.payload;
    },
    setSupportedTargetLanguage: (
      state,
      action: PayloadAction<AutomationApiSliceState["supportedTargetLanguage"]>
    ) => {
      state.supportedTargetLanguage = action.payload;
    },
    resetDocsState: (state) => {
      state.docsState = initialState.docsState;
    },
    setDocsProjectId: (state, action: PayloadAction<string>) => {
      state.docsState.selectedProjectId = action.payload;
    },
    setDocsSourceLanguage: (
      state,
      action: PayloadAction<string | null | undefined>
    ) => {
      state.docsState.selectedSourceLocale = action.payload;
    },
    setDocsTargetLanguage: (state, action: PayloadAction<string[]>) => {
      state.docsState.selectedTargetLocales = action.payload;
    },
    setPreferredCodeLanguage: (
      state,
      action: PayloadAction<PROGRAMMING_LANGUAGE_TYPE>
    ) => {
      state.docsState.preferredCodeLanguage = action.payload;
    }
  }
});
export default automationApiSlice.reducer;
